// useViewportBboxTracking.js — live OL viewport bbox tracking (ticket 07 extraction), via a
// throttled `moveend` listener. Feeds the Layers panel's viewport+DTG filtering. Composed in
// MapEditorModalInner after the map-lifecycle hook.

import { useEffect, useRef, useState } from 'react';
import { viewportBboxNested } from './mapGeometryUtils';

export default function useViewportBboxTracking({ mapRef, mapReady }) {
    // Ticket 06: live OL viewport bbox, tracked via a throttled `moveend` listener (~200ms) —
    // feeds the Layers panel's viewport+DTG filtering. Starts `null` until the map/view first
    // reports a size (matches `layersVisibleAtDtg`'s "skip bbox gating if unknown" contract, so
    // the panel isn't empty before the map settles).
    const [viewportBbox, setViewportBbox] = useState(null);
    const viewportThrottleRef = useRef(null);

    // -- Ticket 06: track viewport bbox for the Layers panel's viewport+DTG filtering ----------
    // Always active once the map is ready (not gated behind an `activeGrid` toggle like the
    // coord-grid effect) since the Layers panel filtering needs the viewport bbox regardless of
    // whether the coord grid is shown.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return;
        const updateViewportBbox = () => setViewportBbox(viewportBboxNested(map));
        updateViewportBbox(); // seed immediately so the panel isn't empty before the first pan
        const throttledUpdate = () => {
            clearTimeout(viewportThrottleRef.current);
            viewportThrottleRef.current = setTimeout(updateViewportBbox, 200);
        };
        map.on('moveend', throttledUpdate);
        return () => { map.un('moveend', throttledUpdate); clearTimeout(viewportThrottleRef.current); };
    }, [mapRef, mapReady]);

    return viewportBbox;
}
